// skipcq: JS-W1028, JS-0128
import { toDateMaybe, toNumberOrZero } from '../../utils/firestoreFieldConversions';

/** @returns {Record<string, number>} */
export function coerceCohortHistogram(value) {
  if (typeof value !== 'object' || value === null) {
    return {};
  }
  const result = {};
  for (const [key, rawCount] of Object.entries(value)) {
    const numericCount = Number(rawCount);
    if (!Number.isFinite(numericCount) || numericCount <= 0) {
      continue;
    }
    result[key] = Math.floor(numericCount);
  }
  return result;
}

/** @typedef {{ id: string; data?: () => Record<string, unknown> }} PlannerDocSnapshot */

/** @param {PlannerDocSnapshot} docSnapshot */
export function mapPlannerRunDoc(docSnapshot) {
  const docData =
    typeof docSnapshot.data === 'function' ? docSnapshot.data() ?? {} : {};
  const statesRaw = docData.eligibleStatesOrdered;
  const statesOrdered = Array.isArray(statesRaw)
    ? statesRaw.map(function mapState(entry) {
        return typeof entry === 'string' ? entry : '';
      })
    : [];
  return {
    id: docSnapshot.id,
    startedAt: toDateMaybe(docData.startedAt),
    finishedAt: toDateMaybe(docData.finishedAt),
    dryRun: docData.dryRun === true,
    crawlType: typeof docData.crawlType === 'string' ? docData.crawlType : '—',
    targetType: typeof docData.targetType === 'string' ? docData.targetType : '—',
    cadence: typeof docData.cadence === 'string' ? docData.cadence : '—',
    eligibleStatesOrdered: statesOrdered,
    breweriesDiscovered: toNumberOrZero(docData.breweriesDiscovered),
    schedulesUpserted: toNumberOrZero(docData.schedulesUpserted),
    schedulesSkippedPaused: toNumberOrZero(docData.schedulesSkippedPaused),
    schedulesSkippedErrorSuppressed: toNumberOrZero(docData.schedulesSkippedErrorSuppressed),
    schedulesSkippedLongTermOob: toNumberOrZero(docData.schedulesSkippedLongTermOob),
    schedulesSkippedSample: toNumberOrZero(docData.schedulesSkippedSample),
    sampleFraction:
      typeof docData.sampleFraction === 'number' && Number.isFinite(docData.sampleFraction)
        ? docData.sampleFraction
        : null,
    cohortHistogram: coerceCohortHistogram(docData.cohortHistogramJson),
  };
}

/** @param {number | null} sampleFraction */
export function formatSampleFraction(sampleFraction) {
  if (sampleFraction === null || !Number.isFinite(sampleFraction) || sampleFraction <= 0) {
    return '—';
  }
  if (sampleFraction >= 1) {
    return '100%';
  }
  return `${Math.round(sampleFraction * 100)}%`;
}

export function formatTs(value) {
  if (!value) return '—';
  return value instanceof Date ? value.toLocaleString() : String(value);
}

/**
 * Sorted cohort histogram entries for MVP coverage bars (numeric cohort index order).
 * @param {Record<string, number>} cohortHistogram
 * @returns {Array<[string, number]>}
 */
export function cohortHistogramEntriesSorted(cohortHistogram) {
  return Object.entries(cohortHistogram).sort(function sortCohort(left, right) {
    const indexLeft = Number(left[0]);
    const indexRight = Number(right[0]);
    const orderLeft = Number.isFinite(indexLeft) ? indexLeft : Number.NaN;
    const orderRight = Number.isFinite(indexRight) ? indexRight : Number.NaN;
    if (!Number.isNaN(orderLeft) && !Number.isNaN(orderRight)) {
      return orderLeft - orderRight;
    }
    return String(left[0]).localeCompare(String(right[0]));
  });
}

/**
 * Label for one cohort histogram bar: state code from the run's eligible list when the index resolves.
 * @param {string} cohortKey
 * @param {number} cohortCount
 * @param {string[]} eligibleStatesOrdered
 * @returns {string}
 */
export function formatCohortBarCaption(cohortKey, cohortCount, eligibleStatesOrdered) {
  const cohortIndex = Number(cohortKey);
  const isValidIndex =
    Number.isFinite(cohortIndex) &&
    cohortIndex >= 0 &&
    cohortIndex < eligibleStatesOrdered.length;
  const stateTrimmed =
    isValidIndex && typeof eligibleStatesOrdered[cohortIndex] === 'string'
      ? eligibleStatesOrdered[cohortIndex].trim()
      : '';
  const labelHead = stateTrimmed.length > 0 ? stateTrimmed : `Cohort ${cohortKey}`;
  return `${labelHead} · ${cohortCount}`;
}
